/**
 * ResponsiveDrawer — bottom sheet on mobile, side drawer on larger screens.
 * Prefer this over centered Modal/Dialog for multi-field forms and task flows.
 */

import React from "react";
import { Drawer } from "antd";
import { useDeviceType } from "../../features/leads/hooks/useDeviceType.js";

const MOBILE_HEIGHT_DEFAULT = "90vh";
const DESKTOP_WIDTH_DEFAULT = 720;

/**
 * @param {Object} props
 * @param {boolean} props.open
 * @param {Function} props.onClose
 * @param {React.ReactNode} [props.title]
 * @param {React.ReactNode} [props.children]
 * @param {React.ReactNode} [props.footer] - Sticky footer (actions)
 * @param {React.ReactNode} [props.extra] - Header extra actions
 * @param {number|string} [props.desktopWidth=720]
 * @param {string} [props.mobileHeight="90vh"]
 * @param {"left"|"right"} [props.desktopPlacement="right"]
 * @param {boolean} [props.maskClosable=true]
 * @param {boolean} [props.destroyOnHidden]
 * @param {boolean} [props.destroyOnClose]
 * @param {Object} [props.styles] - Ant Drawer styles merge
 * @param {string} [props.className]
 * @param {string} [props.rootClassName]
 */
const ResponsiveDrawer = ({
  open,
  onClose,
  title,
  children,
  footer = null,
  extra,
  desktopWidth = DESKTOP_WIDTH_DEFAULT,
  mobileHeight = MOBILE_HEIGHT_DEFAULT,
  desktopPlacement = "right",
  maskClosable = true,
  destroyOnHidden,
  destroyOnClose,
  styles: stylesProp,
  className,
  rootClassName,
  ...rest
}) => {
  // Align with app-wide useDeviceType (MUI md ≈ 900px), not Ant md (768px)
  const { isMobile } = useDeviceType();

  const placement = isMobile ? "bottom" : desktopPlacement;
  const width = isMobile ? "100%" : desktopWidth;
  const height = isMobile ? mobileHeight : "100%";

  const mergedStyles = {
    ...stylesProp,
    content: {
      ...(isMobile
        ? {
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
          }
        : null),
      ...stylesProp?.content,
    },
    body: {
      paddingBottom: footer ? 8 : undefined,
      ...stylesProp?.body,
    },
    footer: {
      borderTop: "1px solid #f0f0f0",
      ...stylesProp?.footer,
    },
  };

  const mergedRootClassName = [
    "responsive-drawer",
    isMobile ? "responsive-drawer--bottom" : "responsive-drawer--side",
    rootClassName,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <Drawer
      title={title}
      open={open}
      onClose={onClose}
      placement={placement}
      width={width}
      height={height}
      footer={footer}
      extra={extra}
      maskClosable={maskClosable}
      destroyOnHidden={destroyOnHidden}
      destroyOnClose={destroyOnClose}
      styles={mergedStyles}
      className={className}
      rootClassName={mergedRootClassName}
      {...rest}
    >
      {children}
    </Drawer>
  );
};

export default ResponsiveDrawer;
